import * as d3 from 'd3';
import format from 'date-fns/format';

import { isNumber, isPresent } from 'shared/lib/utils';

import {
  HOVER_RECT_COLOR,
  HOVER_TEXT_COLOR,
  INTERVAL_SELECTION_RECT_COLOR,
} from '@/lib/config';

import { getSvgMousePosition } from '@/lib/renderers/utils';

const INTERVAL_SELECTION_RECT_OPACITY = 0.2;
const INTERVAL_SELECTION_TIME_THRESHOLD_MS = 100;
export const HOVER_WIDTH = 1;

function updateRectSurroundingTextPosition(
  backgroundRectEl,
  { x, y },
  { horizontal, vertical }
) {
  const backgroundX = x - horizontal;
  const backgroundY = y - vertical;

  backgroundRectEl.setAttribute('x', backgroundX);
  backgroundRectEl.setAttribute('y', backgroundY);
}

function updateRectSurroundingText(
  backgroundRectEl,
  { x, y, width, height },
  { horizontal, vertical }
) {
  const backgroundX = x - horizontal;
  const backgroundY = y - vertical;
  const backgroundWidth = width + 2 * horizontal;
  const backgroundHeight = height + 2 * vertical;

  backgroundRectEl.setAttribute('x', backgroundX);
  backgroundRectEl.setAttribute('y', backgroundY);
  backgroundRectEl.setAttribute('width', backgroundWidth);
  backgroundRectEl.setAttribute('height', backgroundHeight);
}

function rectSurroundingText(textBBox, padding, attributes) {
  const backgroundRectEl = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'rect'
  );
  backgroundRectEl.classList.add('background');

  updateRectSurroundingText(backgroundRectEl, textBBox, padding);

  backgroundRectEl.setAttribute('stroke', 'black');
  backgroundRectEl.setAttribute('stroke-opacity', 0.5);
  backgroundRectEl.setAttribute('visibility', 'hidden');

  for (let attribute in attributes) {
    backgroundRectEl.setAttribute(attribute, attributes[attribute]);
  }

  return backgroundRectEl;
}

function makeIntervalSelectionRect({ x, y, width, height }) {
  const intervalSelectionRect = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'rect'
  );
  intervalSelectionRect.setAttribute('stroke', '#cccccc');
  intervalSelectionRect.setAttribute('x', x);
  intervalSelectionRect.setAttribute('y', y);
  intervalSelectionRect.setAttribute('width', width);
  intervalSelectionRect.setAttribute('height', height);
  intervalSelectionRect.setAttribute('fill', INTERVAL_SELECTION_RECT_COLOR);
  intervalSelectionRect.setAttribute(
    'fill-opacity',
    INTERVAL_SELECTION_RECT_OPACITY
  );
  intervalSelectionRect.setAttribute('visibility', 'hidden');

  return intervalSelectionRect;
}

export default class ChartSelectionRenderer {
  _isInitialized;

  draggableAreaElBoundingClientRect;
  backgroundScreenCtm;

  domain;
  dateRangeScale;

  intervalSelectionRect;

  intervalSelectionStartPosition;

  // Handler for dragging the selection box.
  dragIntervalSelection;

  // onMouseDown/onMouseUp
  dragTimeout;
  mouseDownEvent;

  hoverDate = new Date();
  hoverLineRect;
  hoverText;
  hoverTextBackgroundRectPadding;
  hoverTextBackgroundRect;

  isInitialized() {
    return this._isInitialized === true;
  }

  hideIntervalSelectionRect() {
    this.intervalSelectionRect.setAttribute('visibility', 'hidden');
    this.intervalSelectionRect.setAttribute('width', 0);
  }

  showCreateAnnotationPopover(e) {
    this.setCreateAnnotationPopoverState({
      date: this.hoverDate,
      // NOTE: e.pageX and e.pageY are relative to the document, not the svg.
      // Makes it work with scrolling.
      coordinates: [e.pageX, e.pageY],
    });
  }

  hideCreateAnnotationPopover(_e) {
    this.setCreateAnnotationPopoverState(null);
  }

  showHover(_e) {
    this.hoverLineRect.setAttribute('visibility', 'visible');
    this.hoverText.setAttribute('visibility', 'visible');
    this.hoverTextBackgroundRect.setAttribute('visibility', 'visible');
  }

  hideHover(_e) {
    this.hoverLineRect.setAttribute('visibility', 'hidden');
    this.hoverText.setAttribute('visibility', 'hidden');
    this.hoverTextBackgroundRect.setAttribute('visibility', 'hidden');
  }

  intervalSelectionInProgress(_e) {
    return isPresent(this.intervalSelectionStartPosition);
  }

  makeDragIntervalSelection(svgScreenCtm) {
    let positionFurthestToTheLeft;
    let positionFurthestToTheRight;

    return (e) => {
      if (e.button !== 0) {
        return;
      }

      if (!this.intervalSelectionInProgress(e)) {
        return;
      }

      if (
        e.x < this.draggableAreaElBoundingClientRect.x ||
        e.x >
          this.draggableAreaElBoundingClientRect.x +
            this.draggableAreaElBoundingClientRect.width
      ) {
        return;
      }

      const mousePosition = getSvgMousePosition(svgScreenCtm, e);

      if (this.intervalSelectionStartPosition.x < mousePosition.x) {
        positionFurthestToTheLeft = this.intervalSelectionStartPosition;
        positionFurthestToTheRight = mousePosition;
      } else {
        positionFurthestToTheLeft = mousePosition;
        positionFurthestToTheRight = this.intervalSelectionStartPosition;
      }

      const intervalSelectionStartDate = this.dateRangeScale(
        positionFurthestToTheLeft.x
      );
      const intervalSelectionEndDate = this.dateRangeScale(
        positionFurthestToTheRight.x
      );

      this.setSelectionStartDate(intervalSelectionStartDate);
      this.setSelectionEndDate(intervalSelectionEndDate);

      let intervalSelectionX;
      let intervalSelectionWidth;

      if (mousePosition.x < this.intervalSelectionStartPosition.x) {
        intervalSelectionX = mousePosition.x;
        intervalSelectionWidth =
          this.intervalSelectionStartPosition.x - mousePosition.x;
      } else {
        intervalSelectionX = this.intervalSelectionStartPosition.x;
        intervalSelectionWidth =
          mousePosition.x - this.intervalSelectionStartPosition.x;
      }

      this.intervalSelectionRect.setAttribute('x', intervalSelectionX);
      this.intervalSelectionRect.setAttribute('width', intervalSelectionWidth);
    };
  }

  startIntervalSelection(svgScreenCtm) {
    this.dragIntervalSelection = this.makeDragIntervalSelection(svgScreenCtm);

    return (e) => {
      if (e.button !== 0) {
        return;
      }

      this.intervalSelectionRect.setAttribute('visibility', 'visible');

      this.intervalSelectionStartPosition = getSvgMousePosition(
        svgScreenCtm,
        e
      );

      this.setAreAnnotationsDisabled(true);
      this.svgEl.classList.add('cursor-col-resize');

      this.svgEl.addEventListener('mousemove', this.dragIntervalSelection);
    };
  }

  isIntervalSelectionRectVisible() {
    return this.intervalSelectionRect.getAttribute('visibility') === 'visible';
  }

  endIntervalSelection(e) {
    if (e.button !== 0) {
      return;
    }

    this.setAreAnnotationsDisabled(false);

    this.svgEl.classList.remove('cursor-col-resize');
    this.svgEl.removeEventListener('mousemove', this.dragIntervalSelection);

    this.intervalSelectionStartPosition = null;
  }

  renderHover(e) {
    const mousePosition = getSvgMousePosition(this.backgroundScreenCtm, e);

    this.hoverDate.setTime(this.dateRangeScale(mousePosition.x));

    this.hoverLineRect.setAttribute('x', mousePosition.x);

    this.hoverText.textContent = format(this.hoverDate, 'yyyy-MM-dd');

    const hoverTextBBox = this.hoverText.getBBox();
    const { width: hoverTextWidth } = hoverTextBBox;

    this.hoverText.setAttribute('x', mousePosition.x - hoverTextWidth / 2);

    updateRectSurroundingTextPosition(
      this.hoverTextBackgroundRect,
      hoverTextBBox,
      this.hoverTextBackgroundRectPadding
    );
  }

  renderHoverIfWithinDraggableArea(e) {
    if (
      this.createAnnotationPopoverStateRef &&
      isPresent(this.createAnnotationPopoverStateRef.current)
    ) {
      return;
    }

    if (
      e.x < this.draggableAreaElBoundingClientRect.x ||
      e.x >
        this.draggableAreaElBoundingClientRect.x +
          this.draggableAreaElBoundingClientRect.width
    ) {
      return;
    }

    this.renderHover(e);
  }

  isCreateAnnotationPopoverVisible(_e) {
    return isPresent(this.createAnnotationPopoverStateRef.current);
  }

  onMouseDown(e) {
    // NOTE: to not conflict with browser native drag-and-drop?
    e.preventDefault();

    this.mouseDownEvent = e;

    this.dragTimeout = setTimeout(() => {
      if (this.mouseDownEvent) {
        if (this.isCreateAnnotationPopoverVisible(e)) {
          this.hideCreateAnnotationPopover(e);
        }

        this.startIntervalSelection(this.backgroundScreenCtm)(e);
      }
    }, INTERVAL_SELECTION_TIME_THRESHOLD_MS);
  }

  onMouseUp(e) {
    e.preventDefault();

    if (this.dragTimeout) {
      clearTimeout(this.dragTimeout);
    }

    if (this.intervalSelectionInProgress(e)) {
      this.endIntervalSelection(e);
    } else {
      if (this.isIntervalSelectionRectVisible()) {
        this.hideIntervalSelectionRect();
        this.setSelectionStartDate(null);
        this.setSelectionEndDate(null);
      } else {
        if (this.isCreateAnnotationPopoverVisible(e)) {
          this.hideCreateAnnotationPopover(e);
          this.showHover(e);
          this.renderHover(e);
        } else if (this.mouseDownEvent) {
          const mouseDownPosition = getSvgMousePosition(
            this.backgroundScreenCtm,
            this.mouseDownEvent
          );

          if (
            mouseDownPosition.x >= this.domain[0] &&
            mouseDownPosition.x <= this.domain[1]
          ) {
            this.showCreateAnnotationPopover(e);
          }
        }
      }
    }

    this.mouseDownEvent = null;
  }

  renderInitialDateRangeSelection(initialDateRangeSelection) {
    const positionFurthestToTheLeft = this.dateRangeScale.invert(
      initialDateRangeSelection[0]
    );
    const positionFurthestToTheRight = this.dateRangeScale.invert(
      initialDateRangeSelection[1]
    );

    this.intervalSelectionRect.setAttribute('x', positionFurthestToTheLeft);
    this.intervalSelectionRect.setAttribute(
      'width',
      positionFurthestToTheRight - positionFurthestToTheLeft
    );
    this.intervalSelectionRect.setAttribute('visibility', 'visible');
  }

  destroy() {
    this._isInitialized = false;
  }

  initialize(
    svgEl,
    draggableAreaEl,
    _dateRangeScale,
    setAreAnnotationsDisabled,
    setSelectionStartDate,
    setSelectionEndDate,
    createAnnotationPopoverStateRef,
    setCreateAnnotationPopoverState,
    skipEventListeners,
    _story
  ) {
    this.svgEl = svgEl;
    this.setSelectionStartDate = setSelectionStartDate;
    this.setSelectionEndDate = setSelectionEndDate;
    this.setAreAnnotationsDisabled = setAreAnnotationsDisabled;
    this.setCreateAnnotationPopoverState = setCreateAnnotationPopoverState;
    this.createAnnotationPopoverStateRef = createAnnotationPopoverStateRef;

    this.draggableAreaElBoundingClientRect =
      draggableAreaEl.getBoundingClientRect();

    const draggableAreaElBBox = draggableAreaEl.getBBox();
    const draggableAreaElCtm = draggableAreaEl.getCTM();

    const backgroundEl = this.svgEl.querySelector(
      '.role-frame.root > g > path.background'
    );
    this.backgroundScreenCtm = backgroundEl.getScreenCTM();
    const backgroundCtm = backgroundEl.getCTM();

    const horizontalDistanceFromDraggableAreaToSvg =
      (draggableAreaElCtm.e - backgroundCtm.e) / backgroundCtm.a;
    const verticalDistanceFromDraggableAreaToSvg =
      (draggableAreaElCtm.f - backgroundCtm.f) / backgroundCtm.d;

    // Interval selection rect.

    this.intervalSelectionRect = makeIntervalSelectionRect({
      x: Number.MIN_SAFE_INTEGER,
      y: verticalDistanceFromDraggableAreaToSvg,
      width: 0,
      height: draggableAreaElBBox.height,
    });

    backgroundEl.insertAdjacentElement('afterend', this.intervalSelectionRect);

    // Hover rect.

    this.hoverLineRect = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'rect'
    );
    this.hoverLineRect.setAttribute('stroke', '#dddddd');
    this.hoverLineRect.setAttribute('x', Number.MIN_SAFE_INTEGER);
    this.hoverLineRect.setAttribute(
      'y',
      verticalDistanceFromDraggableAreaToSvg
    );

    this.hoverLineRect.setAttribute('width', HOVER_WIDTH);
    this.hoverLineRect.setAttribute('height', draggableAreaElBBox.height);
    this.hoverLineRect.setAttribute('fill', HOVER_RECT_COLOR);
    this.hoverLineRect.setAttribute('visibility', 'hidden');

    backgroundEl.insertAdjacentElement('afterend', this.hoverLineRect);

    this.hoverText = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'text'
    );
    this.hoverText.textContent = 'YYYY-MM-DD';
    this.hoverText.setAttribute('font-family', 'monospace');
    this.hoverText.setAttribute('visibility', 'hidden');
    this.hoverText.setAttribute('font-size', '18px');
    this.hoverText.setAttribute('fill', HOVER_TEXT_COLOR);

    backgroundEl.insertAdjacentElement('afterend', this.hoverText);

    const hoverTextBoundingClientRect = this.hoverText.getBoundingClientRect();
    const hoverTextBBox = this.hoverText.getBBox();

    const { height: hoverTextHeight } = hoverTextBoundingClientRect;

    this.hoverTextBackgroundRectPadding = {
      vertical: 2,
      horizontal: 4,
    };

    const hoverTextBackgroundRectStrokeWidth = 1;

    this.hoverText.setAttribute('x', Number.MIN_SAFE_INTEGER);
    this.hoverText.setAttribute(
      'y',
      verticalDistanceFromDraggableAreaToSvg +
        hoverTextHeight +
        this.hoverTextBackgroundRectPadding.vertical / 2 +
        hoverTextBackgroundRectStrokeWidth * 2
    );

    this.hoverTextBackgroundRect = rectSurroundingText(
      hoverTextBBox,
      this.hoverTextBackgroundRectPadding,
      {
        'stroke-width': hoverTextBackgroundRectStrokeWidth,
      }
    );

    backgroundEl.insertAdjacentElement(
      'afterend',
      this.hoverTextBackgroundRect
    );

    //////////////////////////////////////////////////////////////////////////////

    // FIXME(CTX-099): update domain on window resize because the values above
    // change.
    this.domain = [
      horizontalDistanceFromDraggableAreaToSvg,
      horizontalDistanceFromDraggableAreaToSvg + draggableAreaElBBox.width,
    ];

    // NOTE: having the range as numbers instead of Date objects makes some
    // things easier, like updating component state to trigger re-renders.
    const range = _dateRangeScale
      .domain()
      .map((d) => (isNumber(d) ? d : d.getTime()));

    this.dateRangeScale = d3
      .scaleLinear()
      .domain(this.domain)
      .range([range[0], range.slice(-1)[0]]);

    //////////////////////////////////////////////////////////////////////////////

    if (skipEventListeners) {
      this._isInitialized = true;
      return;
    }

    this.svgEl.addEventListener('mousedown', this.onMouseDown.bind(this));
    this.svgEl.addEventListener('mouseup', this.onMouseUp.bind(this));
    this.svgEl.addEventListener(
      'mousemove',
      this.renderHoverIfWithinDraggableArea.bind(this)
    );
    this.showHover();

    this._isInitialized = true;
  }
}
